/**
 * Die Lager-Auswahl, live aus JTL - NEUENTWICKLUNG vom 19.08.2026, KEIN Portierungsmodul (anders
 * als `config/warehouses.ts`/`lib/suppliers.ts`, deren Herkunftsmarken `scripts/gen-herkunft.ts`
 * gegen `index.html` gegenprueft, siehe deren Kopfkommentare). Ersetzt `config/jtlLager.ts` (bis
 * zum 19.08.2026 die Handbruecke Tool-Lagername -> JTL-Kennung), die mit diesem Modul entfaellt -
 * die Kennung reist jetzt direkt an der Auswahloption aus JTL mit.
 *
 * WARUM: bis zum 19.08.2026 zeigte die Lager-Auswahl drei fest eingetragene Platzhalter
 * (`DEFAULT_WAREHOUSES`, `config/warehouses.ts`, woertlich aus `index.html` uebernommen) und
 * `JTL_LAGER_JE_TOOLLAGER` (die geloeschte Handbruecke) bildete sie von Hand auf eine JTL-Kennung
 * ab. Ein in JTL neu angelegtes, umbenanntes oder stillgelegtes Lager brauchte dafuer eine
 * Codeaenderung. Seit `GET /api/jtl/warehouses` (`backend/src/jtl/lager.ts`) steht die Liste live
 * zur Verfuegung - die Auswahl liest sie jetzt direkt, ohne eigene Kopie im Frontend.
 */

/** Ein aktives JTL-Warenlager, wie `GET /api/jtl/warehouses` es liefert (`backend/src/jtl/typen.ts#JtlLager`). */
export interface JtlLager {
  kWarenLager: number;
  name: string;
  kuerzel: string;
}

interface BestellungMitLager {
  warehouse?: unknown;
}

/**
 * Die Namen fuer die Lager-Auswahl: erst die JTL-Lager in JTL-Reihenfolge (`kWarenLager`
 * aufsteigend - die Route sortiert bereits so), danach die Namen, die vorhandene Bestellungen
 * tragen und die JTL NICHT (mehr) fuehrt. Getrimmt, ohne Doppelte; leere/fehlende Werte kommen
 * nicht hinein.
 *
 * WARUM DER ZWEITE TEIL BLEIBT: wird ein Lager in JTL geloescht oder stillgelegt (`nAktiv = 0`,
 * `jtl/lager.ts`), soll eine Bestellung, die noch daran haengt, ihren Wert in der Auswahl weiter
 * sehen - sonst verliert sie ihn beim naechsten Speichern STUMM: die Auswahl zeigte den
 * gespeicherten Wert gar nicht mehr an, und ein Speichern ueberschriebe ihn mit der ersten
 * verbleibenden Option. Dieselbe Haltung wie `warehouseNames()` im Bestand (`lib/suppliers.ts`),
 * hier nur ohne die drei Platzhalter als Grundmenge.
 *
 * BEWUSST UNVERAENDERT LASSEN, Auftrag C2b (2026-08-22): der zweite Teil liest weiterhin
 * `bestellung.warehouse` (bis Auftrag D, 2026-08-22, `order_extras.warehouse` - seither eine
 * entfernte Spalte, siehe "WELCHER FALL TATSAECHLICH UNGEDECKT BLEIBT" unten), NICHT `lagerName`
 * aus JTL - obwohl der Drawer
 * (`Drawer/MainTab/Stammdaten.tsx`) seither `lagerName` anzeigt/schreibt und `warehouse` nicht mehr
 * anfasst. Diese Funktion ist aber GEMEINSAM mit der Anlagemaske (`NewOrder/Anlagemaske.tsx`), die
 * dieser Auftrag ausdruecklich NICHT umbaut - eine Umstellung der Quelle haette dort STILL
 * mitgewirkt und den dokumentierten Ablehnungsfall ("Halle Ost", `verdrahtungAnlage.test.tsx`,
 * Faelle b3/b3.1) unbemerkt veraendert.
 *
 * WELCHER FALL TATSAECHLICH UNGEDECKT BLEIBT (richtiggestellt, Auftrag C2c, M4 - eine vorige Fassung
 * dieses Kommentars behauptete das Gegenteil): dass `lagerName` aus einem LEFT JOIN OHNE
 * `nAktiv`-Filter kommt (`backend/src/jtl/bestellungen.ts#BESTELLUNG_KOPF_SPALTEN`) und ein
 * stillgelegtes Lager seinen Namen deshalb weiterhin korrekt liefert, sagt nichts darueber, ob die
 * AUSWAHLLISTE ihn kennt - und genau die kommt aus `GET /api/jtl/warehouses` mit `WHERE nAktiv = 1`
 * (`backend/src/jtl/lager.ts:17`). RICHTIGGESTELLT (Auftrag C7, Nachbesserung Auftrag E - die
 * Zuschreibung stand hier verkehrt herum): ein REACT-gesteuertes `<select>` faellt bei einem Wert
 * ohne passende Option NICHT auf "leer" zurueck, sondern auf die ERSTE Option (`selectedIndex 0`);
 * ein natives, per `value`-Setter gefuelltes `<select>` bleibt dagegen leer (`selectedIndex -1`).
 *
 * ÜBERHOLT (Nachtrag "Bestellfelder nach Anlage sperren", Abschnitt 5, 2026-08-25): der Absatz
 * beschrieb hier, wie `Stammdaten.tsx` diesen Fall bis dahin eigens auffing (eine ALS ERSTE
 * gerenderte Leeroption, WENN `lagerName` keine echte Option traf) - dieses `<select>` gibt es dort
 * seither nicht mehr, das Lager-Feld im Drawer ist read-only (`<input>`, kein `lagerAuswahl`-Aufruf
 * mehr). Der Fall (stillgelegtes/geloeschtes Lager ohne passende Auswahloption) besteht nur noch in
 * der Anlagemaske (`NewOrder/Anlagemaske.tsx`) - dort steht UNBEDINGT eine eigene leere `<option
 * value="">` (nicht bedingt wie im entfallenen Drawer-Code), das Auffangnetz war dort also nie
 * noetig. Der STILLGELEGTE Fall bleibt trotzdem der ungedeckte, nicht der geloeschte - und der
 * geloeschte hat exakt dasselbe Symptom, ist also kein Sonderfall obendrauf.
 *
 * RICHTIGGESTELLT (Auftrag E): das Auffangnetz hier haette diesen Fall ohnehin nicht geschlossen -
 * und zwar nicht nur fuer den DRAWER. `order_extras.warehouse` ist seit Auftrag D (2026-08-22,
 * `backend/migrations/017_order_extras_warehouse_notes_entfernen.sql`) ENTFERNT; kein `Order`-Objekt
 * traegt seither je einen `warehouse`-Wert. Dieser zweite Teil ist damit fuer ALLE Aufrufer TOT,
 * nicht nur fuer den Drawer - auch die Anlagemaske (`NewOrder/Anlagemaske.tsx`) reicht dieselbe,
 * extras-lose `bestellungen`-Liste durch. Warum die Luecke heute trotzdem nicht auftritt: GEMESSEN
 * (2026-08-22) - 8 Warenlager, ausnahmslos aktiv; 2140 Lieferantenbestellungen, ausnahmslos
 * `kLager = 2`. Kein Beleg zeigt derzeit auf ein stillgelegtes Lager. Die Entscheidung, `lagerAuswahl`
 * trotzdem unangetastet zu lassen, bleibt richtig (geteilt mit der Anlagemaske, kein Umfangszuwachs;
 * der zweite Teil ist zudem seit der Migration ohnehin unerreichbarer Totcode, dessen Entfernung ein
 * eigener, hier nicht beauftragter Schritt waere) - nur die Begruendung war zweimal falsch: erst
 * beim Fall selbst (C2c, M4), dann bei der Reichweite ("nur der Drawer" statt "alle").
 */
export function lagerAuswahl(
  bestellungen: readonly BestellungMitLager[] | null | undefined,
  jtlLager: readonly JtlLager[] | null | undefined,
): string[] {
  const namen: string[] = [];
  const gesehen = new Set<string>();

  for (const lager of jtlLager || []) {
    const name = String(lager?.name ?? "").trim();
    if (!name || gesehen.has(name)) continue;
    gesehen.add(name);
    namen.push(name);
  }
  for (const bestellung of bestellungen || []) {
    const name = String(bestellung?.warehouse ?? "").trim();
    if (!name || gesehen.has(name)) continue;
    gesehen.add(name);
    namen.push(name);
  }
  return namen;
}

/**
 * Die JTL-Kennung (`kWarenLager`) zu einem Lagernamen - getrimmter Vergleich. `undefined` bei
 * einem unbekannten Namen, KEIN Vorgabewert: `spLieferantenBestellungErstellen` verlangt eine
 * echte Lager-Kennung, und auf ein Lager auszuweichen, das der Nutzer nicht gewaehlt hat, waere
 * eine Falschbuchung in einem produktiven Beleg. Der Aufrufer lehnt einen unbekannten Namen
 * deshalb ab (`App.tsx#neueBestellungAnlegen`), statt hier stillschweigend ein Lager unterzuschieben
 * - uebernommen aus der Begruendung von `config/jtlLager.ts`, bevor die Datei geloescht wurde.
 */
export function lagerKennung(
  name: unknown,
  jtlLager: readonly JtlLager[] | null | undefined,
): number | undefined {
  const gesucht = String(name ?? "").trim();
  if (!gesucht) return undefined;
  const treffer = (jtlLager || []).find((lager) => String(lager?.name ?? "").trim() === gesucht);
  return treffer?.kWarenLager;
}

/**
 * Die Kennung, auf die die Anlagemaske vorbelegt wird - das WMS-Lager, vom Auftraggeber am
 * 19.08.2026 als Vorgabe festgelegt. An der KENNUNG statt am Namen: eine Umbenennung in JTL
 * ("WMS-Lager" -> etwas anderes) haengt die Vorgabe damit nicht ab, anders als ein Namensvergleich
 * es taete.
 */
export const VORGABE_LAGER_KENNUNG = 2;

/**
 * Der Name zu `VORGABE_LAGER_KENNUNG` - `""`, wenn JTL diese Kennung NICHT (mehr) fuehrt. Kein
 * Rueckgriff auf einen Ersatznamen: eine Vorbelegung mit einem Text, den die Auswahl gar nicht
 * (mehr) anbietet, waere kein gueltiger Wert und liesse `required` unbemerkt durchgehen.
 */
export function vorgabeLagerName(jtlLager: readonly JtlLager[] | null | undefined): string {
  const treffer = (jtlLager || []).find((lager) => lager?.kWarenLager === VORGABE_LAGER_KENNUNG);
  return treffer ? String(treffer.name ?? "").trim() : "";
}
